'use client'

import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import useSWR from 'swr'
import type { NatsConnection } from 'nats.ws'
import Cookies from 'js-cookie'
import {
  SocketManagerProvider,
  NatsManager,
  PricesWebSocketManager,
  NATS_EVENT_NAMES,
} from '@ind-money/socket-manager'
import { useAuth } from './auth'

import { logNewRelicEvent } from '@/root/utils/common/custom/newrelic-page-events'
import { WithChildren } from '@/root/types/utilities'
import { devLog } from '@/root/utils/devLogger'
import { getFetchRequest } from '@/root/utils/network-adapter/fetch-request'
import {
  IND_STOCKS_BASEPATH,
  IND_STOCKS_JWT_AUTH,
} from '@/root/api/stocks/apiConstants'

const WS_URLS = {
  prices: 'wss://flash-feed-prices.indmoney.com/ws/prices',
  trades: 'wss://flash-feed-trades.indmoney.com:7443/ws/trades',
  nats: 'wss://ws.indmoney.com',
  usPrices: 'wss://flash-feed-prices-us.indmoney.com/ws/prices',
}

interface IGenerateHashResponse {
  data: {
    hash_value: string
  }
}

const getHashedToken = async () => {
  const url = `${IND_STOCKS_BASEPATH}/auth/generate-hash`
  const resp = await getFetchRequest({
    url,
    config: {
      headers: {
        Authorization: `Bearer ${Cookies.get('token')}`,
        platform: 'web',
      },
    },
  })

  return resp as IGenerateHashResponse
}

/** Hash token for WebSocket authentication */
const fetchHashToken = async () => {
  const hashData = await getHashedToken()
  return hashData.data.hash_value
}

/** NATS JWT token response type */
interface NatsTokenResponse {
  success: boolean
  token?: string
  expires_at?: number
}

/** NATS JWT token fetcher */
const fetchNatsToken = async (
  authorization?: string,
): Promise<NatsTokenResponse> => {
  return getFetchRequest({
    config: {
      headers: {
        Authorization: authorization ?? `Bearer ${Cookies.get('token')}`,
        platform: 'web',
      },
    },
    timeout: 0,
    url: `${IND_STOCKS_BASEPATH}${IND_STOCKS_JWT_AUTH}`,
  })
}

const WebSocketProvider = ({ children }: WithChildren<{}>) => {
  const { token } = useAuth()
  const [isNatsTokenExpired, setIsNatsTokenExpired] = useState(false)
  const lastFocusTime = useRef(Date.now())
  const natsReconnectCount = useRef(0)

  // Fetch hash token using SWR (for WebSocket)
  const { data: hashToken, isLoading: isHashLoading } = useSWR(
    'websocket-hash-token',
    fetchHashToken,
    {
      revalidateOnFocus: false,
      revalidateOnReconnect: false,
      dedupingInterval: 60000, // 1 minute deduplication
    },
  )

  // Fetch NATS JWT token using SWR with expires_at based refresh
  const {
    data: natsTokenData,
    mutate: mutateNatsToken,
    isLoading: isNatsLoading,
  } = useSWR<NatsTokenResponse>(
    isNatsTokenExpired ? null : ['nats-jwt-token', natsReconnectCount.current],
    () => fetchNatsToken(),
    {
      shouldRetryOnError: false,
      errorRetryInterval: 0,
      revalidateOnFocus: false,
      revalidateOnReconnect: false,
      onSuccess: () => {
        devLog('[WebSocketProvider] NATS token fetched successfully')
      },
      onError: (err?: { statusCode?: number }) => {
        devLog('[WebSocketProvider] NATS token fetch error', err)
        if (err?.statusCode === 512) {
          setIsNatsTokenExpired(true)
        }
      },
      // Auto-refresh based on expires_at
      refreshInterval(latestData) {
        if (latestData?.expires_at) {
          const currentUnixTimestamp = Math.floor(Date.now() / 1000)
          const differenceInSeconds =
            latestData.expires_at - currentUnixTimestamp
          const differenceInMinutes = differenceInSeconds / 60
          devLog(
            '[WebSocketProvider] NATS token refresh in',
            differenceInMinutes,
            'minutes',
          )
          return differenceInMinutes * 60000
        }
        return 0
      },
    },
  )

  // Handle NATS connection changes
  const handleNatsConnectionChange = useCallback(
    (nats: NatsConnection | null) => {
      // Update PricesWebSocketManager with NATS connection status
      const status = nats?.isClosed()
        ? 'closed'
        : nats?.isDraining()
          ? 'draining'
          : 'connected'
      PricesWebSocketManager.setNatsConnection(status)

      logNewRelicEvent(NATS_EVENT_NAMES.CONNECTED, {
        status,
        server: nats?.getServer() ?? WS_URLS.nats,
      })

      devLog('[WebSocketProvider] NATS connection changed', { status })
    },
    [],
  )

  // Window focus handler for NATS connection health check
  useEffect(() => {
    const handleWindowFocus = () => {
      const natsManager = NatsManager.getInstance()
      const timeSinceLastFocus = Date.now() - lastFocusTime.current

      // Track the connection status on focus (fires NR events)
      natsManager.trackConnectionStatusOnFocus(
        timeSinceLastFocus,
        natsReconnectCount.current,
      )

      const { status, needsReconnect } = natsManager.checkConnectionHealth()

      devLog('[WebSocketProvider] Window focus - NATS status', {
        status,
        needsReconnect,
        timeSinceLastFocus,
      })

      if (needsReconnect) {
        natsReconnectCount.current += 1
        void mutateNatsToken() // Trigger token refetch and reconnection
      }

      lastFocusTime.current = Date.now()
    }

    window.addEventListener('focus', handleWindowFocus)
    return () => window.removeEventListener('focus', handleWindowFocus)
  }, [mutateNatsToken])

  // Periodic health check for NATS connection (every 60 seconds)
  useEffect(() => {
    const interval = setInterval(() => {
      const natsManager = NatsManager.getInstance()
      const { status, needsReconnect } = natsManager.checkConnectionHealth()

      if (needsReconnect) {
        devLog(
          '[WebSocketProvider] Periodic health check - NATS needs reconnect',
        )
        natsReconnectCount.current += 1
        void mutateNatsToken() // Trigger token refetch and reconnection
      }
    }, 60000)

    return () => clearInterval(interval)
  }, [mutateNatsToken])

  const socketConfig = useMemo(
    () => ({
      pricesWebSocketUrl: WS_URLS.prices,
      tradesWebSocketUrl: WS_URLS.trades,
      natsServer: WS_URLS.nats,
      usPricesWebSocketUrl: WS_URLS.usPrices,
      hashToken,
      enablePrices: !!hashToken,
      enableTrades: false,
      // TODO: Enable only in US market hours
      enableUSPrices: !!hashToken,
      natsEnabled: !!natsTokenData?.token,
      natsToken: natsTokenData?.token,
      analytics: { trackEvent: logNewRelicEvent },
      logger: { log: devLog },
    }),
    [hashToken, natsTokenData?.token],
  )

  const isLoading = isHashLoading || isNatsLoading

  return (
    <SocketManagerProvider
      config={socketConfig}
      enabled={!!token && !!hashToken && !isLoading}
      onNatsConnectionChange={handleNatsConnectionChange}
    >
      {children}
    </SocketManagerProvider>
  )
}

export default WebSocketProvider
